<template>
  <section
    class="scoreboard-page"
    :class="{ 'fullscreen-fallback': fullscreenFallbackActive }"
  >
    <div class="scoreboard-toolbar">
      <div class="scoreboard-settings">
        <label>
          Vitoria
          <input ref="targetScoreInput" v-model.number="targetScore" min="1" type="number">
        </label>
        <label>
          Diferenca
          <input ref="minDifferenceInput" v-model.number="minDifference" min="1" type="number">
        </label>
        <button type="button" @click="resetScores">Zerar</button>
      </div>

      <div class="scoreboard-actions">
        <button type="button" @click="$emit('show-draw')">Sorteio</button>
        <button type="button" @click="showGeneralScoreModal = true">Placar geral</button>
        <button v-if="!isFullscreen" type="button" @click="enterFullscreen">
          Tela cheia
        </button>
        <button v-else type="button" @click="exitFullscreen">
          Sair tela cheia
        </button>
        <button
          v-if="wakeLockSupported"
          type="button"
          :class="{ active: wakeLockActive }"
          @click="toggleWakeLock"
        >
          Tela ligada
        </button>
      </div>
    </div>

    <div
      v-if="winnerId && showWinnerSummary"
      class="winner-summary"
    >
      <h2>{{ winnerTeam.name }} venceu</h2>
      <div class="general-score">
        <div
          v-for="team in generalScore"
          :key="team.id"
          class="general-score-row"
          :style="{ borderLeftColor: team.color }"
        >
          <span>{{ team.name }}</span>
          <strong>{{ team.totalWins }}</strong>
        </div>
      </div>
      <div class="winner-actions">
        <button class="return-button" type="button" @click="returnToScoreboard">Voltar ao placar</button>
        <button class="new-match-button" type="button" @click="startNextMatch">Iniciar nova partida</button>
      </div>
    </div>

    <template v-else>
      <div class="active-teams-bar" :class="{ portrait: isPortrait, landscape: !isPortrait }">
        <div
          v-for="(slot, index) in activeSlots"
          :key="`selector-${slot.team.id}`"
          class="active-team"
          :style="teamBadgeStyle(slot.team)"
        >
          <button
            v-if="editingSide !== index"
            class="team-name"
            type="button"
            :disabled="!canChangeTeams"
            @click="startTeamEdit(index)"
          >
            {{ slot.team.name }}
          </button>
          <select
            v-else
            :value="slot.team.id"
            @change="changeActiveTeam(index, $event.target.value)"
            @blur="editingSide = null"
          >
            <option
              v-for="team in availableTeams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
        </div>
      </div>

      <div
        class="scoreboard"
        :class="{ portrait: isPortrait, landscape: !isPortrait }"
      >
        <div
          v-for="(slot, index) in activeSlots"
          :key="slot.team.id"
          class="score-panel"
          :class="{ winner: winnerId === slot.team.id }"
          :style="panelStyle(slot.team)"
          role="button"
          tabindex="0"
          @click="onPanelClick(index)"
          @keydown.enter="onPanelClick(index)"
          @keydown.space.prevent="onPanelClick(index)"
          @touchstart.passive="onTouchStart($event, index)"
          @touchend.prevent="onTouchEnd($event, index)"
          @touchcancel="onTouchCancel"
          @contextmenu.prevent
        >
          <span class="score">{{ slot.score }}</span>
          <div v-if="showDesktopControls" class="desktop-score-controls">
            <button type="button" @click.stop="decrement(index)">-</button>
            <button type="button" @click.stop="increment(index)">+</button>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="showGeneralScoreModal"
      class="modal-backdrop"
      @click.self="showGeneralScoreModal = false"
    >
      <div class="general-score-modal" role="dialog" aria-modal="true" aria-labelledby="general-score-title">
        <div class="modal-header">
          <h2 id="general-score-title">Placar geral</h2>
          <button type="button" aria-label="Fechar placar geral" @click="showGeneralScoreModal = false">x</button>
        </div>
        <div class="general-score">
          <div
            v-for="team in generalScore"
            :key="team.id"
            class="general-score-row"
            :style="{ borderLeftColor: team.color }"
          >
            <span>{{ team.name }}</span>
            <strong>{{ team.totalWins }}</strong>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const TOURNAMENT_STORAGE_KEY = 'scoreboardTournament';
const TEAMS_STORAGE_KEY = 'scoreboardTeams';

export default {
  name: 'ScoreboardView',
  data() {
    const availableTeams = this.loadAvailableTeams();
    const savedTournament = this.loadTournament();
    const activeTeamIds = savedTournament.activeTeamIds || availableTeams.slice(0, 2).map(team => team.id);

    return {
      targetScore: Number(localStorage.getItem('scoreboardTargetScore')) || 25,
      minDifference: Number(localStorage.getItem('scoreboardMinDifference')) || 2,
      availableTeams,
      activeTeamIds,
      scores: savedTournament.scores || [0, 0],
      stats: savedTournament.stats || {},
      queueIndex: Number(savedTournament.queueIndex) || 0,
      showWinnerSummary: false,
      showGeneralScoreModal: false,
      editingSide: null,
      hasTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      isFullscreen: Boolean(this.getFullscreenElement()),
      fullscreenFallbackActive: false,
      isPortrait: window.matchMedia('(orientation: portrait)').matches,
      touchStartX: 0,
      touchStartY: 0,
      touchSide: null,
      longPressTimer: null,
      longPressHandled: false,
      suppressNextClick: false,
      wakeLock: null,
      wakeLockActive: false,
      wakeLockSupported: 'wakeLock' in navigator,
    };
  },
  computed: {
    activeSlots() {
      return this.activeTeamIds.map((teamId, index) => ({
        team: this.findTeam(teamId),
        score: this.scores[index] || 0,
      }));
    },
    winnerSide() {
      const [first, second] = this.scores;
      const firstDiff = first - second;
      const secondDiff = second - first;

      if (first >= this.effectiveTargetScore && firstDiff >= this.effectiveMinDifference) {
        return 0;
      }

      if (second >= this.effectiveTargetScore && secondDiff >= this.effectiveMinDifference) {
        return 1;
      }

      return null;
    },
    winnerId() {
      if (this.winnerSide === null) {
        return null;
      }

      return this.activeTeamIds[this.winnerSide];
    },
    winnerTeam() {
      return this.findTeam(this.winnerId);
    },
    generalScore() {
      return this.availableTeams
        .map(team => {
          const stats = this.teamStats(team.id);
          const pendingWin = this.winnerId === team.id ? 1 : 0;

          return {
            ...team,
            totalWins: stats.totalWins + pendingWin,
          };
        })
        .sort((first, second) => second.totalWins - first.totalWins || first.name.localeCompare(second.name));
    },
    effectiveTargetScore() {
      return Math.max(1, Number(this.targetScore) || 1);
    },
    effectiveMinDifference() {
      return Math.max(1, Number(this.minDifference) || 1);
    },
    canChangeTeams() {
      return this.scores.every(score => score === 0) && !this.winnerId;
    },
    showDesktopControls() {
      return !this.hasTouch;
    },
  },
  watch: {
    targetScore(value) {
      localStorage.setItem('scoreboardTargetScore', Math.max(1, Number(value) || 1));
    },
    minDifference(value) {
      localStorage.setItem('scoreboardMinDifference', Math.max(1, Number(value) || 1));
    },
    winnerId(value, oldValue) {
      if (value && value !== oldValue) {
        this.showWinnerSummary = true;
      }

      if (!value) {
        this.showWinnerSummary = false;
      }
    },
  },
  mounted() {
    this.ensureStats();
    this.persistTournament();
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('orientationchange', this.updateOrientation);
    window.addEventListener('resize', this.updateOrientation);
  },
  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('orientationchange', this.updateOrientation);
    window.removeEventListener('resize', this.updateOrientation);
    this.clearLongPressTimer();
    this.disableFullscreenFallback();
    this.releaseWakeLock();
  },
  methods: {
    loadAvailableTeams() {
      try {
        const savedTeams = JSON.parse(localStorage.getItem(TEAMS_STORAGE_KEY));

        if (Array.isArray(savedTeams) && savedTeams.length >= 2) {
          return savedTeams;
        }
      } catch (error) {
        localStorage.removeItem(TEAMS_STORAGE_KEY);
      }

      return [
        { id: 'team-1', name: 'Time 1', color: '#0066ff', textColor: '#ffffff' },
        { id: 'team-2', name: 'Time 2', color: '#d000d8', textColor: '#ffffff' },
      ];
    },
    loadTournament() {
      try {
        return JSON.parse(localStorage.getItem(TOURNAMENT_STORAGE_KEY)) || {};
      } catch (error) {
        localStorage.removeItem(TOURNAMENT_STORAGE_KEY);
        return {};
      }
    },
    persistTournament() {
      localStorage.setItem(TOURNAMENT_STORAGE_KEY, JSON.stringify({
        activeTeamIds: this.activeTeamIds,
        scores: this.scores,
        stats: this.stats,
        queueIndex: this.queueIndex,
      }));
    },
    ensureStats() {
      this.availableTeams.forEach(team => {
        if (!this.stats[team.id]) {
          this.stats[team.id] = {
            totalWins: 0,
            streak: 0,
            consecutive: 0,
          };
        } else if (this.stats[team.id].consecutive === undefined) {
          this.stats[team.id].consecutive = 0;
        }
      });
    },
    teamStats(teamId) {
      if (!this.stats[teamId]) {
        this.stats[teamId] = {
          totalWins: 0,
          streak: 0,
          consecutive: 0,
        };
      } else if (this.stats[teamId].consecutive === undefined) {
        this.stats[teamId].consecutive = 0;
      }

      return this.stats[teamId];
    },
    findTeam(teamId) {
      return this.availableTeams.find(team => team.id === teamId) || this.availableTeams[0];
    },
    panelStyle(team) {
      return {
        backgroundColor: team.color,
        color: team.textColor || '#ffffff',
      };
    },
    teamBadgeStyle(team) {
      return {
        borderColor: team.color,
      };
    },
    onPanelClick(side) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }

      this.increment(side);
    },
    blurScoreSettingInput() {
      const activeElement = document.activeElement;
      const scoreSettingInputs = [this.$refs.targetScoreInput, this.$refs.minDifferenceInput];

      if (scoreSettingInputs.includes(activeElement)) {
        activeElement.blur();
      }
    },
    increment(side) {
      if (this.winnerId) {
        return;
      }

      this.blurScoreSettingInput();
      this.scores[side] += 1;
      this.persistTournament();
    },
    decrement(side) {
      this.blurScoreSettingInput();
      this.scores[side] = Math.max(0, this.scores[side] - 1);
      this.persistTournament();
    },
    startTeamEdit(side) {
      if (!this.canChangeTeams) {
        return;
      }

      this.editingSide = side;
    },
    onTouchStart(event, side) {
      const touch = event.changedTouches[0];

      this.clearLongPressTimer();
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchSide = side;
      this.longPressHandled = false;

      this.longPressTimer = window.setTimeout(() => {
        if (this.touchSide === side) {
          this.decrement(side);
          this.longPressHandled = true;
          this.suppressNextClick = true;
        }
      }, 550);
    },
    onTouchEnd(event, side) {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      const distanceX = Math.abs(deltaX);
      const distanceY = Math.abs(deltaY);

      this.clearLongPressTimer();

      if (this.touchSide !== side) {
        this.resetTouchState();
        return;
      }

      if (this.longPressHandled) {
        this.suppressNextClick = true;
        this.resetTouchState();
        return;
      }

      if (distanceY >= 32 && distanceY > distanceX) {
        if (deltaY < 0) {
          this.increment(side);
        } else {
          this.decrement(side);
        }
      } else if (distanceX < 24 && distanceY < 24) {
        this.increment(side);
      }

      this.suppressNextClick = true;
      this.resetTouchState();
    },
    onTouchCancel() {
      this.clearLongPressTimer();
      this.resetTouchState();
      this.suppressNextClick = true;
    },
    clearLongPressTimer() {
      if (this.longPressTimer) {
        window.clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    resetTouchState() {
      this.touchSide = null;
      this.longPressHandled = false;
    },
    resetScores() {
      this.blurScoreSettingInput();
      this.scores = [0, 0];
      this.showWinnerSummary = false;
      this.persistTournament();
    },
    returnToScoreboard() {
      if (this.winnerSide !== null) {
        this.decrement(this.winnerSide);
      }

      this.showWinnerSummary = false;
    },
    changeActiveTeam(side, teamId) {
      if (!this.canChangeTeams) {
        this.editingSide = null;
        return;
      }

      if (this.activeTeamIds.includes(teamId) && this.activeTeamIds[side] !== teamId) {
        this.editingSide = null;
        return;
      }

      this.activeTeamIds[side] = teamId;
      this.showWinnerSummary = false;
      this.editingSide = null;
      this.persistTournament();
    },
    startNextMatch() {
      if (this.winnerSide === null) {
        return;
      }

      const winnerSide = this.winnerSide;
      const loserSide = winnerSide === 0 ? 1 : 0;
      const winnerId = this.activeTeamIds[winnerSide];
      const loserId = this.activeTeamIds[loserSide];
      const currentTeamIds = [...this.activeTeamIds];
      const winnerStats = this.teamStats(winnerId);
      const loserStats = this.teamStats(loserId);

      winnerStats.totalWins += 1;
      winnerStats.streak += 1;
      loserStats.streak = 0;
      if (this.availableTeams.length > 2) {
        currentTeamIds.forEach(teamId => {
          this.teamStats(teamId).consecutive += 1;
        });

        const leavingSides = this.resolveLeavingSides(winnerSide, loserSide, currentTeamIds);
        const incomingIds = [];

        leavingSides.forEach(side => {
          const blockedIds = [
            ...currentTeamIds.filter((teamId, index) => !leavingSides.includes(index)),
            ...incomingIds,
          ];
          const incomingTeam = this.findNextWaitingTeam(blockedIds);

          if (incomingTeam) {
            const outgoingId = currentTeamIds[side];
            this.teamStats(outgoingId).streak = 0;
            this.teamStats(outgoingId).consecutive = 0;
            this.activeTeamIds[side] = incomingTeam.id;
            this.teamStats(incomingTeam.id).consecutive = 0;
            incomingIds.push(incomingTeam.id);
          }
        });
      }

      this.scores = [0, 0];
      this.showWinnerSummary = false;
      this.persistTournament();
    },
    resolveLeavingSides(winnerSide, loserSide, currentTeamIds) {
      const leavingSides = [];

      currentTeamIds.forEach((teamId, side) => {
        if (this.teamStats(teamId).consecutive >= 2) {
          leavingSides.push(side);
        }
      });

      if (this.teamStats(currentTeamIds[winnerSide]).streak >= 2 && !leavingSides.includes(winnerSide)) {
        leavingSides.push(winnerSide);
      }

      if (!leavingSides.length) {
        leavingSides.push(loserSide);
      }

      return leavingSides;
    },
    findNextWaitingTeam(blockedIds) {
      const unavailableIds = [...blockedIds, ...this.activeTeamIds];

      for (let offset = 0; offset < this.availableTeams.length; offset += 1) {
        const index = (this.queueIndex + offset) % this.availableTeams.length;
        const team = this.availableTeams[index];

        if (!unavailableIds.includes(team.id)) {
          this.queueIndex = (index + 1) % this.availableTeams.length;
          return team;
        }
      }

      return null;
    },
    updateOrientation() {
      this.isPortrait = window.matchMedia('(orientation: portrait)').matches;
    },
    getFullscreenElement() {
      return document.fullscreenElement || document.webkitFullscreenElement;
    },
    getFullscreenRequest(element) {
      return element.requestFullscreen || element.webkitRequestFullscreen;
    },
    getFullscreenExit() {
      return document.exitFullscreen || document.webkitExitFullscreen;
    },
    async enterFullscreen() {
      if (!this.getFullscreenElement()) {
        const requestFullscreen = this.getFullscreenRequest(document.documentElement);

        if (requestFullscreen) {
          try {
            await requestFullscreen.call(document.documentElement);
          } catch (error) {
            this.enableFullscreenFallback();
          }
        } else {
          this.enableFullscreenFallback();
        }
      }

      await this.requestWakeLock();
    },
    async exitFullscreen() {
      if (this.fullscreenFallbackActive) {
        this.disableFullscreenFallback();
      }

      if (this.getFullscreenElement()) {
        const exitFullscreen = this.getFullscreenExit();

        if (exitFullscreen) {
          await exitFullscreen.call(document);
        }
      }
    },
    enableFullscreenFallback() {
      this.fullscreenFallbackActive = true;
      this.isFullscreen = true;
      document.documentElement.classList.add('scoreboard-fullscreen-fallback-root');
      document.body.classList.add('scoreboard-fullscreen-fallback-root');
      window.scrollTo(0, 0);
    },
    disableFullscreenFallback() {
      this.fullscreenFallbackActive = false;
      this.isFullscreen = Boolean(this.getFullscreenElement());
      document.documentElement.classList.remove('scoreboard-fullscreen-fallback-root');
      document.body.classList.remove('scoreboard-fullscreen-fallback-root');
    },
    onFullscreenChange() {
      this.isFullscreen = this.fullscreenFallbackActive || Boolean(this.getFullscreenElement());
    },
    async toggleWakeLock() {
      if (this.wakeLockActive) {
        await this.releaseWakeLock();
      } else {
        await this.requestWakeLock();
      }
    },
    async requestWakeLock() {
      if (!this.wakeLockSupported || this.wakeLock) {
        return;
      }

      try {
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLockActive = true;
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null;
          this.wakeLockActive = false;
        });
      } catch (error) {
        this.wakeLock = null;
        this.wakeLockActive = false;
      }
    },
    async releaseWakeLock() {
      if (!this.wakeLock) {
        return;
      }

      await this.wakeLock.release();
      this.wakeLock = null;
      this.wakeLockActive = false;
    },
    async onVisibilityChange() {
      if (document.visibilityState === 'visible' && this.wakeLockActive && !this.wakeLock) {
        await this.requestWakeLock();
      }
    },
  },
};
</script>

<style scoped>
.scoreboard-page {
  min-height: 100vh;
  background: #101010;
  color: #fff;
  display: flex;
  flex-direction: column;
  font-family: Arial, sans-serif;
}

:global(.scoreboard-fullscreen-fallback-root) {
  overflow: hidden;
  height: 100%;
}

.scoreboard-page.fullscreen-fallback {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  min-height: 100dvh;
  z-index: 9999;
}

@supports not (height: 100dvh) {
  .scoreboard-page.fullscreen-fallback {
    height: 100vh;
    min-height: 100vh;
  }
}

.scoreboard-toolbar {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #171717;
}

.scoreboard-settings,
.scoreboard-actions,
.winner-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.scoreboard-settings label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #f7f7f7;
  font-size: 14px;
}

.scoreboard-settings input {
  width: 58px;
  min-height: 38px;
  padding: 4px 6px;
  border: 1px solid #555;
  border-radius: 5px;
  background: #fff;
  color: #111;
  font-size: 18px;
}

.scoreboard-toolbar button,
.winner-actions button {
  min-height: 38px;
  padding: 8px 12px;
  border: 0;
  border-radius: 5px;
  background: #f7f7f7;
  color: #151515;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.scoreboard-toolbar button.active {
  background: #38c172;
  color: #051a0e;
}

.active-teams-bar {
  display: grid;
  gap: 8px;
  padding: 8px;
  background: #101010;
}

.active-teams-bar.landscape {
  grid-template-columns: 1fr 1fr;
}

.active-teams-bar.portrait {
  grid-template-rows: auto auto;
}

.active-team {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 6px solid;
  border-radius: 6px;
  background: #f7f7f7;
}

.scoreboard {
  flex: 1;
  display: grid;
  min-height: 0;
}

.scoreboard.landscape {
  grid-template-columns: 1fr 1fr;
}

.scoreboard.portrait {
  grid-template-rows: 1fr 1fr;
}

.score-panel {
  width: 100%;
  min-width: 0;
  min-height: 0;
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
}

.score-panel.winner {
  background: #12a150 !important;
  color: #fff !important;
}

.team-name,
.active-team select {
  width: min(72vw, 360px);
  min-height: 44px;
  padding: 6px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #111;
  font-size: clamp(20px, 4vmin, 42px);
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
}

.active-team select {
  color: #111;
  background: #fff;
  border: 1px solid #bbb;
}

.team-name:disabled {
  cursor: default;
  opacity: 1;
}

.score {
  font-size: clamp(120px, min(38vmin, 28vw, 42dvh), 360px);
  font-weight: 900;
  line-height: 0.9;
  font-variant-numeric: tabular-nums;
}

.scoreboard.portrait .score {
  font-size: clamp(120px, min(36vmin, 52vw, 30dvh), 360px);
}

.desktop-score-controls {
  display: flex;
  gap: 12px;
}

.desktop-score-controls button {
  width: 62px;
  height: 46px;
  border: 2px solid currentColor;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.22);
  color: inherit;
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
}

.winner-summary {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  width: min(680px, calc(100% - 24px));
  margin: 0 auto;
  padding: 20px 0;
}

.winner-summary h2 {
  margin: 0;
  color: #fff;
  font-size: clamp(36px, 8vmin, 88px);
  text-align: center;
  text-transform: uppercase;
}

.general-score {
  display: grid;
  gap: 8px;
}

.general-score-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  border-left: 10px solid;
  background: #f5f5f5;
  color: #111;
  border-radius: 5px;
  font-size: 20px;
  font-weight: 800;
}

.general-score-row strong {
  font-size: 28px;
}

.winner-actions {
  justify-content: center;
}

.winner-actions button {
  min-height: 48px;
  font-size: 16px;
}

.winner-actions .return-button {
  background: #f59e0b;
  color: #2b1700;
}

.winner-actions .new-match-button {
  background: #22c55e;
  color: #052e16;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.62);
}

.general-score-modal {
  width: min(520px, 100%);
  max-height: min(680px, calc(100vh - 32px));
  overflow: auto;
  padding: 16px;
  border-radius: 8px;
  background: #151515;
  color: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.modal-header h2 {
  margin: 0;
  color: #fff;
  font-size: 28px;
}

.modal-header button {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 6px;
  background: #f7f7f7;
  color: #151515;
  font-size: 24px;
  font-weight: 900;
  cursor: pointer;
}

@media (max-width: 720px) {
  .scoreboard-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .scoreboard-settings,
  .scoreboard-actions {
    justify-content: space-between;
  }

  .scoreboard-settings label {
    flex: 1;
  }

  .active-teams-bar {
    gap: 4px;
    padding: 4px;
  }

  .active-team {
    min-height: 38px;
    border-width: 4px;
    border-radius: 4px;
  }

  .team-name,
  .active-team select {
    min-height: 32px;
    padding: 3px 8px;
    font-size: 16px;
    border-radius: 4px;
  }

  .desktop-score-controls {
    display: none;
  }
}
</style>
